import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditCard, TriangleAlert } from '@/lib/icons'
import type { PayoutBulkResult, PayoutListItem, PayoutReadyItem } from '@ayub/shared'
import { formatNumber, formatSom, PayoutType } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  createBulkPayout,
  createPayout,
  fetchBloggerCardForPayout,
  markPayoutPaid,
  requestBloggerCard,
} from './api'
import { BulkPayResultDialog } from './BulkPayResultDialog'
import { PayoutsEmptyState } from './EmptyStates'
import { PayoutsErrorBanner, PayoutsRetryState } from './ErrorStates'
import { invalidatePayoutRelatedCaches, useBudget, usePayoutReady } from './hooks'
import { PayoutCardCell } from './PayoutCardCell'

/**
 * "✅ To'lovga tayyor" tabi — `docs/03-app-flow.md` Sahifa 4: hisobi yopilgan, hali
 * to'lanmagan HAMMA bloger (so'rov yuborgan-yubormaganidan qat'i nazar). Jami summa va
 * belgilar SHU RO'YXATdan (`/payouts/ready`) to'g'ridan-to'g'ri hisoblanadi — alohida
 * `PayoutSummary`ga tayanilmaydi, shunda ko'rsatilgan "Jami" har doim RO'YXAT bilan mos
 * keladi (vazifa talabi: "stale ... erodes trust").
 *
 * ⚠️ KARTASIZ HOLAT — bu chekka holat emas, atayin fixture'ga solingan (vazifa ta'rifi):
 * ikkita bloger kartasi yo'q holda pul kutmoqda. Ular RO'YXATDA ⚠️ bilan ko'rinadi,
 * checkbox o'chirilgan (tanlab bo'lmaydi — "select all" ularni HECH QACHON qamramaydi) va
 * `[💳 Karta so'rash]` tugmasi bilan blogerdan karta so'rash mumkin. Ularni jim
 * o'tkazib yuborish pulni "abadiy to'lanmagan" holga keltiradi — shuning uchun bu belgi
 * doim ko'rinadi, hech qachon yashirin filtr bilan yo'qolmaydi.
 */
export function PayoutReadyView() {
  const queryClient = useQueryClient()
  const ready = usePayoutReady()
  const { budget } = useBudget()

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [singlePayTarget, setSinglePayTarget] = useState<PayoutReadyItem | null>(null)
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const [bulkResult, setBulkResult] = useState<PayoutBulkResult | null>(null)
  const [cardRequestedFor, setCardRequestedFor] = useState<number | null>(null)

  const items = ready.items
  const selectableItems = useMemo(() => items.filter((item) => item.hasCard), [items])
  const cardlessItems = useMemo(() => items.filter((item) => !item.hasCard), [items])

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0)
  const selectedTotal = items
    .filter((item) => selected.has(item.bloggerId))
    .reduce((sum, item) => sum + item.amount, 0)

  const allSelectableSelected =
    selectableItems.length > 0 && selectableItems.every((item) => selected.has(item.bloggerId))

  /**
   * "Ikki bosqichli to'lov" (`docs/06-implementation-plan.md`): bitta qatorni to'lash uchun
   * AVVAL `POST /api/payouts` (REQUESTED yozuv ochadi), keyin `POST /api/payouts/:id/paid`
   * (haqiqiy yakunlash — balans 0, videolar `To'landi`) ZANJIRLAB chaqiriladi. Admin uchun bu
   * BITTA tugma — ichki ikki bosqich UI'da ko'rinmaydi.
   *
   * Agar ikkinchi qadam (xato/tarmoq) muvaffaqiyatsiz tugasa — birinchi qadam allaqachon
   * REQUESTED yozuv yaratgan bo'ladi va u "💸 So'rovlar" tabida ko'rinadi (`status=REQUESTED`
   * FILTRI TURGA qarab ajratmaydi) — admin xatoni shu yerdan qayta urinib tugata oladi, pul
   * "yo'qolib" qolmaydi.
   */
  const singlePayMutation = useMutation<PayoutListItem, Error, PayoutReadyItem>({
    mutationFn: async (item) => {
      const created = await createPayout({ bloggerId: item.bloggerId, type: PayoutType.MANUAL })
      return markPayoutPaid(created.id, {})
    },
    onSuccess: () => {
      invalidatePayoutRelatedCaches(queryClient)
      setSinglePayTarget(null)
      setSelected((prev) => {
        if (!singlePayTarget) return prev
        const next = new Set(prev)
        next.delete(singlePayTarget.bloggerId)
        return next
      })
    },
  })

  const bulkPayMutation = useMutation<PayoutBulkResult, Error, number[]>({
    mutationFn: (bloggerIds) => createBulkPayout({ bloggerIds, type: PayoutType.MANUAL }),
    onSuccess: (result) => {
      invalidatePayoutRelatedCaches(queryClient)
      setBulkConfirmOpen(false)
      setBulkResult(result)
      setSelected(new Set())
    },
  })

  const requestCardMutation = useMutation<{ sent: number }, Error, number>({
    mutationFn: requestBloggerCard,
    onSuccess: (_data, bloggerId) => {
      setCardRequestedFor(bloggerId)
    },
  })

  function toggleOne(bloggerId: number, hasCard: boolean) {
    if (!hasCard) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(bloggerId)) next.delete(bloggerId)
      else next.add(bloggerId)
      return next
    })
  }

  function toggleSelectAll() {
    setSelected(
      allSelectableSelected ? new Set() : new Set(selectableItems.map((i) => i.bloggerId)),
    )
  }

  const bloggerNames = useMemo(() => {
    const map = new Map<number, string>()
    for (const item of items) map.set(item.bloggerId, item.fullName)
    return map
  }, [items])

  if (ready.isLoading) return <PayoutReadySkeleton />

  if (ready.error && items.length === 0) {
    return <PayoutsRetryState error={ready.error} onRetry={() => void ready.refetch()} />
  }

  if (items.length === 0) {
    return (
      <PayoutsEmptyState message="To'lanadigan summa yo'q! Barcha hisoblar yopilgan yoki to'langan." />
    )
  }

  return (
    // `aria-busy` — fon rejimida qayta so'ralayotganda skrinriderga signal beradi.
    <div className="flex flex-col gap-3" aria-busy={ready.isFetching}>
      {ready.error && (
        <PayoutsErrorBanner
          error={ready.error}
          isFetching={ready.isFetching}
          onRetry={() => void ready.refetch()}
        />
      )}

      {/* Jami summa — `docs/03`: "Jami: 4 210 000 so'm · 18 bloger". */}
      <div className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-4">
        <p className="text-sm text-app-hint">
          Jami:{' '}
          <span className="tabular font-semibold text-app-text">{formatSom(totalAmount)}</span> ·{' '}
          {formatNumber(items.length)} bloger
        </p>
        {budget && budget.remaining !== null && (
          <p className="text-xs text-app-hint">
            Oylik byudjetda qolgan:{' '}
            <span
              className={cn(
                'tabular font-semibold',
                budget.remaining <= 0 ? 'text-stage-rejected-text' : 'text-app-text',
              )}
            >
              {formatSom(budget.remaining)}
            </span>
            {selectedTotal > budget.remaining && (
              <span className="text-stage-rejected-text">
                {' '}
                — tanlangan {formatSom(selectedTotal)} byudjetdan oshadi; oshgan qismini keyingi oy
                to&apos;laysiz
              </span>
            )}
          </p>
        )}
        {cardlessItems.length > 0 && (
          <p className="inline-flex items-center gap-1.5 text-xs font-medium text-urgency-overdue-text">
            <TriangleAlert size={14} className="shrink-0" aria-hidden="true" />
            {formatNumber(cardlessItems.length)} blogerda karta ma&apos;lumoti yo&apos;q — pastda
            belgilangan
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={toggleSelectAll}
            disabled={selectableItems.length === 0}
          >
            {allSelectableSelected ? 'Tanlovni bekor qilish' : 'Hammasini tanlash'}
          </Button>
          <Button size="sm" disabled={selected.size === 0} onClick={() => setBulkConfirmOpen(true)}>
            Tanlanganlarni to&apos;lash
            {selected.size > 0 ? ` (${formatNumber(selected.size)})` : ''}
          </Button>
        </div>
      </div>

      {/* Desktop / tablet: jadval */}
      <div className="hidden overflow-x-auto rounded-card border border-app-border sm:block">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-app-card">
            <tr>
              <th className="w-10 border-b border-app-border px-3 py-2" />
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Bloger
              </th>
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Summa
              </th>
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Karta
              </th>
              <th className="border-b border-app-border px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <ReadyRow
                key={item.bloggerId}
                item={item}
                checked={selected.has(item.bloggerId)}
                onToggle={() => toggleOne(item.bloggerId, item.hasCard)}
                onPay={() => setSinglePayTarget(item)}
                onRequestCard={() => requestCardMutation.mutate(item.bloggerId)}
                cardRequested={cardRequestedFor === item.bloggerId}
                requestingCard={
                  requestCardMutation.isPending && requestCardMutation.variables === item.bloggerId
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil: kartochkalar ro'yxati */}
      <div className="flex flex-col gap-2 sm:hidden">
        {items.map((item) => (
          <ReadyMobileCard
            key={item.bloggerId}
            item={item}
            checked={selected.has(item.bloggerId)}
            onToggle={() => toggleOne(item.bloggerId, item.hasCard)}
            onPay={() => setSinglePayTarget(item)}
            onRequestCard={() => requestCardMutation.mutate(item.bloggerId)}
            cardRequested={cardRequestedFor === item.bloggerId}
            requestingCard={
              requestCardMutation.isPending && requestCardMutation.variables === item.bloggerId
            }
          />
        ))}
      </div>

      {/* Bitta blogerni to'lash tasdig'i — matn `docs/03` "To'ladim" naqshiga mos. */}
      <ConfirmDialog
        open={singlePayTarget !== null}
        title="To'lovni tasdiqlaysizmi?"
        description={
          singlePayTarget && (
            <>
              <span className="font-medium text-app-text">{singlePayTarget.fullName}</span>ga{' '}
              <span className="tabular font-medium text-app-text">
                {formatSom(singlePayTarget.amount)}
              </span>{' '}
              to&apos;ladingizmi? Bu amal blogerning balansini 0 ga tushiradi va uning{' '}
              <span className="font-medium text-app-text">
                {formatNumber(singlePayTarget.videoCount)} ta
              </span>{' '}
              videosini &quot;To&apos;landi&quot;ga o&apos;tkazadi. Bu amalni ORQAGA QAYTARIB
              BO&apos;LMAYDI.
            </>
          )
        }
        confirmLabel="Ha, to'ladim"
        isLoading={singlePayMutation.isPending}
        errorMessage={singlePayMutation.error?.message}
        onConfirm={() => singlePayTarget && singlePayMutation.mutate(singlePayTarget)}
        onCancel={() => setSinglePayTarget(null)}
      />

      {/* Ommaviy to'lov tasdig'i. */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        title="Tanlanganlarga to'laysizmi?"
        description={
          <>
            <span className="tabular font-medium text-app-text">
              {formatNumber(selected.size)} ta
            </span>{' '}
            blogerga jami{' '}
            <span className="tabular font-medium text-app-text">{formatSom(selectedTotal)}</span>{' '}
            to&apos;lanadi. Har birining balansi 0 ga tushadi va bog&apos;liq videolar
            &quot;To&apos;landi&quot;ga o&apos;tadi. Bu amalni ORQAGA QAYTARIB BO&apos;LMAYDI.
          </>
        }
        confirmLabel="Ha, to'lash"
        isLoading={bulkPayMutation.isPending}
        errorMessage={bulkPayMutation.error?.message}
        onConfirm={() => bulkPayMutation.mutate(Array.from(selected))}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <BulkPayResultDialog
        result={bulkResult}
        bloggerNames={bloggerNames}
        onClose={() => setBulkResult(null)}
      />
    </div>
  )
}

interface ReadyRowProps {
  item: PayoutReadyItem
  checked: boolean
  onToggle: () => void
  onPay: () => void
  onRequestCard: () => void
  cardRequested: boolean
  requestingCard: boolean
}

function ReadyRow({
  item,
  checked,
  onToggle,
  onPay,
  onRequestCard,
  cardRequested,
  requestingCard,
}: ReadyRowProps) {
  return (
    <tr
      className={cn(
        'border-b border-app-border last:border-b-0',
        !item.hasCard && 'bg-urgency-overdue/5',
      )}
    >
      <td className="px-1 py-2">
        {/* `label` o'ramasi checkboxni 44x44px sensor maydonigacha kengaytiradi
            (`docs/04-ui-ux.md`: "Sensor maydoni Minimal 44×44px") — vizual o'lcham (16px)
            o'zgarmaydi, faqat bosiladigan hudud. */}
        <label className="flex size-11 cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            checked={checked}
            disabled={!item.hasCard}
            onChange={onToggle}
            aria-label={`${item.fullName} — tanlash`}
            className="size-4 accent-brand disabled:cursor-not-allowed disabled:opacity-30"
          />
        </label>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col">
          <span className="font-medium text-app-text">{item.fullName}</span>
          <span className="text-xs text-app-hint">@{item.instagramUsername}</span>
        </div>
      </td>
      <td className="px-3 py-2">
        <span className="tabular font-medium text-brand">{formatSom(item.amount)}</span>
      </td>
      <td className="px-3 py-2">
        {item.hasCard ? (
          <PayoutCardCell
            maskedCard={item.cardNumberMasked}
            onReveal={() => fetchBloggerCardForPayout(item.bloggerId)}
          />
        ) : (
          <CardlessCell
            onRequestCard={onRequestCard}
            requested={cardRequested}
            requesting={requestingCard}
          />
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" disabled={!item.hasCard} onClick={onPay}>
          To&apos;ladim
        </Button>
      </td>
    </tr>
  )
}

function ReadyMobileCard({
  item,
  checked,
  onToggle,
  onPay,
  onRequestCard,
  cardRequested,
  requestingCard,
}: ReadyRowProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-3',
        !item.hasCard && 'border-urgency-overdue/40 bg-urgency-overdue/5',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1">
          <label className="flex size-11 shrink-0 cursor-pointer items-center justify-center">
            <input
              type="checkbox"
              checked={checked}
              disabled={!item.hasCard}
              onChange={onToggle}
              aria-label={`${item.fullName} — tanlash`}
              className="size-4 accent-brand disabled:cursor-not-allowed disabled:opacity-30"
            />
          </label>
          <div className="flex flex-col justify-center">
            <span className="font-medium text-app-text">{item.fullName}</span>
            <span className="text-xs text-app-hint">@{item.instagramUsername}</span>
          </div>
        </div>
        <span className="tabular font-semibold text-brand">{formatSom(item.amount)}</span>
      </div>

      {item.hasCard ? (
        <PayoutCardCell
          maskedCard={item.cardNumberMasked}
          onReveal={() => fetchBloggerCardForPayout(item.bloggerId)}
        />
      ) : (
        <CardlessCell
          onRequestCard={onRequestCard}
          requested={cardRequested}
          requesting={requestingCard}
        />
      )}

      <Button size="sm" disabled={!item.hasCard} onClick={onPay}>
        To&apos;ladim
      </Button>
    </div>
  )
}

function CardlessCell({
  onRequestCard,
  requested,
  requesting,
}: {
  onRequestCard: () => void
  requested: boolean
  requesting: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-urgency-overdue-text">
        <TriangleAlert size={12} className="shrink-0" aria-hidden="true" /> Karta yo&apos;q
      </span>
      <Button
        size="sm"
        variant="secondary"
        onClick={onRequestCard}
        disabled={requesting || requested}
      >
        <CreditCard size={14} aria-hidden="true" />
        {requested ? "So'rov yuborildi" : "Karta so'rash"}
      </Button>
    </div>
  )
}

function PayoutReadySkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-24" />
      <div className="hidden overflow-hidden rounded-card border border-app-border sm:block">
        {Array.from({ length: 5 }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex h-14 items-center gap-4 border-b border-app-border px-3 last:border-b-0"
          >
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-[120px]" />
        ))}
      </div>
    </div>
  )
}
